import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CreditPack, User, isLargePack } from 'src/app/shared/classes/app.models';
import { tierCopy } from 'src/app/shared/classes/plan-copy';

/** What one pack row offers, for the term it is sold over. */
export interface TierTerm {
    /** The pack row, so a CTA can go to checkout without a second lookup. */
    packId: number;
    /** Credits granted for the whole term — 200 a month, 2,400 a year. */
    credits: number;
    /** What is charged for the term. */
    price: number;
    /** That price spread over the term, which is what the card leads with. */
    perMonth: number;
    /** ₹ per credit — what the saving against monthly is computed from. */
    rate: number;
    months: number;
}

/**
 * One purchasable tier, assembled from the credit packs `auth/plan` returns.
 *
 * A tier is one pack NAME, not one pack row: the same name is sold once per
 * term, and the period control picks between them. Every row is kept, keyed by
 * duration, so each figure on the card comes from the row that is actually
 * charged rather than from a discount the UI applied.
 *
 * `terms` rather than the monthly/yearly pair this used to hold: the catalogue
 * now sells quarterly as well, and a third field would only have postponed the
 * question of what to do about a fourth.
 */
export interface PricingTier {
    key: string;
    name: string;
    lead: string;
    /** Keyed by duration — 'monthly', 'quarterly', 'yearly'. */
    terms: { [duration: string]: TierTerm };
    badge?: string;
    cta: string;
    /** Which fill the tier's button takes. Assigned by position once the tiers
     *  are sorted, so a pack added or withdrawn cannot leave two cards alike. */
    ctaStyle: 'outline' | 'dark' | 'accent';
    includesLead: string;
    includes: string[];
    notes: string[];
}

/** Terms the backend allows, shortest first — the order the control shows. */
const TERMS: { [key: string]: { label: string; period: string; months: number } } = {
    monthly:       { label: 'Monthly',     period: 'month',    months: 1 },
    quarterly:     { label: 'Quarterly',   period: 'quarter',  months: 3 },
    'half-yearly': { label: 'Half-yearly', period: '6 months', months: 6 },
    yearly:        { label: 'Yearly',      period: 'year',     months: 12 },
};
const TERM_ORDER = ['monthly', 'quarterly', 'half-yearly', 'yearly'];

/** One row of the licence comparison. */
interface LicenceRow {
    usage: string;
    standard: string;
    enhanced: string;
    /** Renders the cell in the "not covered" tone rather than the plain one. */
    standardBlocked?: boolean;
}

@Component({
    selector: 'ai-pricing-dialog',
    templateUrl: './pricing-dialog.component.html',
    styleUrls: ['./pricing-dialog.component.scss']
})
export class PricingDialogComponent implements OnInit, OnDestroy {
    /** Closed by the host; the dialog only ever asks. */
    @Output() closed = new EventEmitter<void>();

    /** Where "Talk to us" should point. Kept as an input so the number lives once. */
    @Input() salesPhone = '+91 9599801596';

    /**
     * Which term the cards are priced for — a pack `duration` string, so the
     * control and the rows it selects cannot drift apart.
     *
     * Settled by the data in buildTiers(): it lands on the shortest term actually
     * sold rather than assuming monthly exists.
     */
    billing = 'monthly';

    /** The terms on offer, shortest first. Built from the packs. */
    terms: string[] = [];

    /** The tiers, built from `auth/plan`. Empty until the call answers. */
    tiers: PricingTier[] = [];

    /**
     * Which plan column the narrow layout shows.
     *
     * Three plan columns plus the tool name and its cost do not fit a phone, so
     * below 767px one is picked at a time rather than scrolled to sideways — a
     * sideways scroll hides the plan the reader is comparing against.
     */
    comparePick = '';

    /**
     * The compare table's rows — the same list the /subscription page carries, so
     * the two surfaces quote one set of figures.
     *
     * `per` is the credit cost of one use; the counts in the table are derived
     * from it and each plan's own credits, never listed — a written count would
     * disagree with the column heading above it the moment either number moved.
     *
     * The costs themselves are copy, like the model tables: credit_pack_detail is
     * what actually charges. Repricing a tool needs the same edit here and in
     * plan.component.ts.
     */
    readonly compareGroups: { title: string; rows: { tool: string; per: number; sub?: string }[] }[] = [
        {
            title: 'Image AI models',
            rows: [
                { tool: 'Imagen 4',        per: 1 },
                { tool: 'GPT Image 1',     per: 1 },
                { tool: 'GPT Image 2',     per: 1 },
                { tool: 'Nano Banana Pro', per: 2 },
                { tool: 'Nano Banana',     per: 1 }
            ]
        },
        {
            title: 'Video AI models',
            rows: [
                { tool: 'Seedance 1 Pro', per: 4 },
                { tool: 'HappyHorse 1.0', per: 4 },
                { tool: 'Wan 2.2 Fast',   per: 6 },
                { tool: 'Wan 2.5',        per: 18 }
            ]
        },
        {
            title: 'Editing',
            rows: [
                {
                    tool: 'Editing tools',
                    per: 1,
                    sub: 'Colorize, Denoise, Erase, Face Restore, Fill, Remove Background, Replace Background, Restore'
                }
            ]
        }
    ];

    readonly compareNote =
        'Counts assume a whole term’s credits go to one tool — in practice you’ll mix tools, so your '
        + 'totals will vary. Quarterly and yearly plans pool credits across the term.';

    loading = true;
    loadError = false;

    /** The horizontal tier rail, so the arrows have something to scroll. */
    @ViewChild('tierTrack', { static: false }) tierTrack: ElementRef<HTMLElement>;

    /**
     * Whether each arrow has anywhere to go.
     *
     * Both false when the cards already fit, which is also what hides the pair:
     * a control that cannot do anything should not be sitting over the cards.
     */
    canScrollTiersLeft = false;
    canScrollTiersRight = false;

    /** Null until signed in; decides whether a CTA checks out or asks to log in. */
    currentUser: User;

    /**
     * The credit pack the customer is already on. A pack activation stores its
     * template id in the customer plan's plan_id, so the id cached at login is the
     * pack to mark — the same source the /subscription page reads.
     */
    activePlanId: number | null = null;

    private authSub: Subscription;

    /**
     * Fallback annual discount, used only when a pack name is sold monthly but
     * has no yearly row of its own. When the yearly row exists its own stored
     * price is used instead, so the card quotes what will actually be charged
     * rather than a rate the UI invented.
     */
    private static readonly YEARLY_DISCOUNT = 0.1;

    constructor(
        private apiService: ApiService,
        private authenticationService: AuthenticationService,
        private modalService: ModalService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        /*
         * currentUser is a BehaviorSubject, so this fires once straight away with
         * whoever is signed in and again if they sign in while the sheet is open —
         * the cards then flip to "Activated Plan" without it being reopened.
         */
        this.authSub = this.authenticationService.currentUser.subscribe(user => {
            this.currentUser = user;

            // From the emitted user, not localStorage. Both hold the same row --
            // setCurrentUser writes one and pushes the other -- but reading the
            // key directly is a snapshot: it would not move when the session did,
            // so a sign-out inside the open dialog left the old plan marked
            // "Activated Plan" on a card the guest can no longer buy.
            this.activePlanId = (user && Number(user.plan_id)) || null;
        });

        this.load();
    }

    ngOnDestroy(): void {
        // The dialog is built and thrown away every time it opens, so the auth
        // subscription has to go with it.
        if (this.authSub) {
            this.authSub.unsubscribe();
        }
    }

    /**
     * Credit pack templates for the pricing cards.
     *
     * The endpoint answers {data: [...]}; a bare array is tolerated so the dialog
     * does not go blank if that ever changes back — the same shape guard the
     * pricing page uses.
     */
    private load(): void {
        this.loading = true;
        this.loadError = false;

        this.apiService.getPlanDetail().subscribe(response => {
            const packs: CreditPack[] = Array.isArray(response) ? response : ((response && response.data) || []);

            // status/ip_pack_status are filtered server side; repeated here so a
            // pack switched off can never reach a card through a cached response.
            const live = packs.filter(p => Number(p.status) === 1 && Number(p.ip_pack_status) === 1);

            /*
             * Subscription tiers only — the same split the pricing page makes.
             * This endpoint also carries the one-time packs, which are sold from
             * the <one-time-packs> section rather than as tiers; without the
             * filter they arrive here as fourteen extra cards, and buildTiers()
             * groups by pack_name so the sizes would each become their own tier.
             */
            this.tiers = this.buildTiers(live.filter(p => isLargePack(p)));
            // The compare table's mobile picker opens on the plan being pushed,
            // falling back to the cheapest when nothing carries the badge.
            const pushed = this.tiers.filter(t => !!t.badge)[0] || this.tiers[0];
            this.comparePick = pushed ? pushed.name : '';
            this.loading = false;
            this.refreshTierScroll();
        }, err => {
            console.error('pricing dialog getPlanDetail error', err);
            this.tiers = [];
            this.comparePick = '';
            this.loading = false;
            this.loadError = true;
            this.canScrollTiersLeft = false;
            this.canScrollTiersRight = false;
        });
    }

    /** One tier per pack name, cheapest first. */
    private buildTiers(packs: CreditPack[]): PricingTier[] {
        const byName: { [name: string]: CreditPack[] } = {};
        const names: string[] = [];

        packs.forEach(p => {
            const name = (p.pack_name || '').trim();
            if (!name) {
                return;
            }
            if (!byName[name]) {
                byName[name] = [];
                names.push(name);
            }
            byName[name].push(p);
        });

        const tiers = names
            .map(name => this.toTier(name, byName[name]))
            .filter(t => Object.keys(t.terms).length > 0)
            .sort((a, b) => this.sortPrice(a) - this.sortPrice(b));

        /*
         * The terms anything is actually sold for, in the order TERM_ORDER sets.
         * Anything the backend adds that is not a known term still gets a button,
         * or its packs would be unreachable.
         */
        const offered: string[] = [];
        tiers.forEach(t => Object.keys(t.terms).forEach(d => {
            if (offered.indexOf(d) === -1) { offered.push(d); }
        }));

        this.terms = TERM_ORDER.filter(d => offered.indexOf(d) > -1)
            .concat(offered.filter(d => TERM_ORDER.indexOf(d) === -1));

        // Settled by the data: monthly is the usual answer but not a guaranteed one.
        if (this.terms.indexOf(this.billing) === -1) {
            this.billing = this.terms.length ? this.terms[0] : 'monthly';
        }

        /*
         * Fills by position rather than by name: the dearest card takes the accent,
         * and the one between the entry and the top takes the badge and the dark
         * fill — never the dearest. No badge below three cards, where "most
         * popular" of two reads as arbitrary.
         */
        tiers.forEach((t, i) => {
            t.ctaStyle = (tiers.length > 1 && i === tiers.length - 1) ? 'accent' : 'outline';
        });
        if (tiers.length >= 3) {
            const middle = tiers[Math.floor((tiers.length - 1) / 2)];
            middle.badge = 'Most popular';
            middle.ctaStyle = 'dark';
        }

        return tiers;
    }

    /**
     * What a tier is ordered by. Its per-month figure on the shortest term it
     * sells — comparing one tier's monthly price against another's yearly would
     * order the cards by term rather than by size.
     */
    private sortPrice(tier: PricingTier): number {
        for (const d of TERM_ORDER) {
            if (tier.terms[d]) { return tier.terms[d].perMonth; }
        }
        const first = Object.keys(tier.terms)[0];
        return first ? tier.terms[first].perMonth : 0;
    }

    /** Fold one name's rows — one per term — into a single card. */
    private toTier(name: string, rows: CreditPack[]): PricingTier {
        const terms: { [duration: string]: TierTerm } = {};

        rows.forEach(row => {
            const duration = (row.duration || '').trim();
            const credits = Number(row.total_credits) || 0;
            const rate = Number(row.credit_value_inr) || 0;

            // A row granting nothing, or priced at nothing, is a broken pack rather
            // than a free one — and would divide by zero below.
            if (!duration || credits <= 0 || rate <= 0) { return; }

            // First row wins for a duration: two rows for one name and term is a
            // data problem, and quietly taking the later one would hide it.
            if (terms[duration]) { return; }

            const months = TERMS[duration] ? TERMS[duration].months : 1;
            // The server's price when it sends one — a credits + images pack also
            // charges for its images; credits × rate otherwise. (An images-only
            // pack has no credits and is not a tier in this credits dialog.)
            const price = row.price_inr !== undefined && Number(row.price_inr) > 0
                ? Math.round(Number(row.price_inr))
                : Math.round(credits * rate);

            terms[duration] = {
                packId: Number(row.id),
                credits: credits,
                price: price,
                perMonth: Math.round(price / months),
                rate: rate,
                months: months,
            };
        });

        // Shared with the /subscription page, so both describe a plan in the same words.
        const copy = tierCopy(name);

        return {
            key: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            name: name,
            lead: copy.lead,
            terms: terms,
            cta: 'Get ' + name,
            ctaStyle: 'outline',
            includesLead: copy.includesLead,
            includes: copy.includes,
            notes: copy.notes,
        };
    }

    /* ---- What the card reads off the selected term ---------------------- */

    /** The row behind a card right now, or null when the tier skips this term. */
    term(tier: PricingTier): TierTerm | null {
        return (tier && tier.terms[this.billing]) || null;
    }

    /** Credits granted for the selected term. */
    termCredits(tier: PricingTier): number {
        const t = this.term(tier);
        return t ? t.credits : 0;
    }

    /** What is charged for the selected term, in full. */
    termPrice(tier: PricingTier): number {
        const t = this.term(tier);
        return t ? t.price : 0;
    }

    /** That price spread over the term — the figure the card leads with. */
    termPerMonth(tier: PricingTier): number {
        const t = this.term(tier);
        return t ? t.perMonth : 0;
    }

    /** 'month' / 'quarter' / 'year', for the line under the price. */
    termPeriod(duration?: string): string {
        const d = duration || this.billing;
        return TERMS[d] ? TERMS[d].period : 'term';
    }

    termLabel(duration: string): string {
        if (TERMS[duration]) { return TERMS[duration].label; }
        const t = (duration || '').replace(/[_-]+/g, ' ').trim();
        return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
    }

    /** True where the term runs longer than a month, so the card says "billed ...". */
    isLongTerm(): boolean {
        return (TERMS[this.billing] ? TERMS[this.billing].months : 1) > 1;
    }

    /**
     * What a tier saves against paying monthly, in rupees over the term.
     * Null where it is not sold monthly, so there is nothing to compare with.
     */
    termSaving(tier: PricingTier): number | null {
        const here = this.term(tier);
        const monthly = tier ? tier.terms['monthly'] : null;

        if (!here || !monthly || here.months <= 1) { return null; }

        const saving = (monthly.perMonth * here.months) - here.price;
        return saving > 0 ? saving : null;
    }

    /**
     * What a term saves against monthly, as a whole percent — the "Save 5%" line
     * under the period control.
     *
     * From the packs' own credit rates, so it cannot drift from what the cards
     * charge. Compared per TIER, not across all of them: each has its own rate,
     * and comparing the cheapest monthly against the cheapest yearly would be
     * comparing two different products.
     *
     * Null when the tiers disagree — a single "Save 5%" is only honest if it
     * holds for every plan behind it.
     */
    termSavingPercent(duration: string): number | null {
        if (!duration || duration === 'monthly') { return null; }

        let agreed: number | null = null;

        for (const tier of this.tiers) {
            const here = tier.terms[duration];
            const monthly = tier.terms['monthly'];

            if (!here) { continue; }
            if (!monthly || here.rate >= monthly.rate) { return null; }

            const saving = Math.round(((monthly.rate - here.rate) / monthly.rate) * 100);

            if (agreed === null) { agreed = saving; }
            else if (agreed !== saving) { return null; }
        }

        return agreed && agreed > 0 ? agreed : null;
    }

    /**
     * license_type is basic | commercial | enterprise; the pricing copy calls the
     * basic tier "Standard", which is the name customers see everywhere else.
     */
    private licenceLabel(type: string): string {
        return (type || '').toLowerCase() === 'basic' ? 'Standard' : this.titleCase(type);
    }

    private titleCase(value: string): string {
        const s = (value || '').replace(/[_-]+/g, ' ').trim();
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    }

    /* The yearly-only accessors this replaced — monthlyEquivalent, yearlyUpfront,
       yearlySaving, yearlyCredits — are now termPerMonth / termPrice / termSaving
       / termCredits, which answer for whichever term is selected. */

    /**
     * The Custom card, last in the tier row.
     *
     * Not a pack and deliberately not shaped like one: no price, no credit count
     * and no checkout, because there is nothing to buy until sales has scoped it.
     * The same card the /subscription page carries, so the two agree on what
     * "custom" means; its CTA dials rather than scrolls, because this dialog has
     * no enquiry form to scroll to.
     */
    readonly customPlan = {
        eyebrow: 'Built around your workflow.',
        name: 'Custom',
        lead: 'For larger teams with unique volume, collaboration or commercial requirements.',
        pillars: ['Custom credits', 'Custom users', 'Custom billing'],
        pitch: 'Need more volume, shared credit pools, multiple users or a tailored '
             + 'commercial setup? Talk to our team.',
        includesLead: 'Need more capacity or a tailored setup?',
        includes: [
            'Custom credit allocation',
            'Shared / team credit pools',
            'Multiple users',
            'Custom commercial terms',
            'Invoice billing',
            'Dedicated sales support',
            'Exclusive Stock Access'
        ],
        bestFor: 'Enterprise & multi-team organisations'
    };

    /**
     * What a credit buys per action — the strip under the period control.
     *
     * Written down, not derived: the pack rows price 27 individual tools, and
     * "Generation · Video" is not one of them but four models charging 2, 2, 3
     * and 8. A derived figure would be a range, which is what this strip exists
     * not to be — the per-model table below is where the detail belongs.
     */
    readonly creditUsage: { label: string; image: number; video:any}[] = [
        { label: 'Generation', image: 1, video: 2 },
        { label: 'Stock',      image: 2, video: 'Coming Soon' }
    ];

    /** Which half of the per-model rate table is showing. */
    modelTab: 'images' | 'videos' = 'images';

    /**
     * What one generation costs, per model.
     *
     * The published catalogue rates. Distinct from the credits table further down,
     * which is derived from the customer's packs and answers how many RUNS a plan
     * buys; this answers what one run costs. Copy either way — repricing a pack
     * needs the same edit here.
     */
    readonly imageModels: { model: string; output: string; res: string[]; credits: number }[] = [
        { model: 'Imagen 4',        output: 'Still Image', res: ['1K', '2K'],       credits: 1 },
        { model: 'GPT Image 1',     output: 'Still Image', res: ['1K'],             credits: 1 },
        { model: 'GPT Image 2',     output: 'Still Image', res: ['1K'],             credits: 1 },
        { model: 'Nano Banana Pro', output: 'Still Image', res: ['1K', '2K', '4K'], credits: 1 },
        { model: 'Nano Banana',     output: 'Still Image', res: ['1K'],             credits: 1 }
    ];

    readonly imageModelsNote =
        'Credits are charged per generation. Resolution does not change the credit cost for these models.';

    readonly videoModels: { model: string; durations: string[]; res: string[]; credits: number }[] = [
        { model: 'Seedance 1 Pro', durations: ['5s', '10s'],                    res: ['480p', '720p', '1080p'], credits: 8 },
        { model: 'HappyHorse 1.0', durations: ['3s', '5s', '8s', '10s', '15s'], res: ['720p', '1080p'],         credits: 2 },
        { model: 'Wan 2.2 Fast',   durations: ['5s', '7s'],                     res: ['480p', '720p'],          credits: 2 },
        { model: 'Wan 2.5',        durations: ['5s', '10s'],                    res: ['480p', '720p', '1080p'], credits: 3 }
    ];

    readonly videoModelsNote =
        'Credits are charged per generation. Duration and resolution do not change the credit cost within a model.';

    readonly licenceRows: LicenceRow[] = [
        {
            usage: 'Websites, apps, social, digital ads',
            standard: 'Included',
            enhanced: 'Not required'
        },
        {
            usage: 'Print run / copies',
            standard: 'Up to 5,00,000',
            enhanced: 'Unlimited'
        },
        {
            usage: 'Out-of-home, hoardings, billboards',
            standard: 'Not covered',
            standardBlocked: true,
            enhanced: 'Unlimited'
        },
        {
            usage: 'TV commercials, cinema, OTT platforms',
            standard: 'Not covered',
            standardBlocked: true,
            enhanced: 'Unlimited'
        },
        {
            usage: 'Merchandise for resale',
            standard: 'Not covered',
            standardBlocked: true,
            enhanced: 'Unlimited'
        }
    ];

    readonly licenceFacts = [
        {
            title: 'Commercial use included',
            body: 'Every generated asset carries a Standard commercial licence — web, social, ' +
                'digital ads and print within the limits above. No separate licence fee per asset.'
        },
        {
            title: 'Your prompts stay yours',
            body: 'Prompts and uploads are used to run the tools you asked for. IndiaPicture does ' +
                'not use your inputs to train a generative model.'
        },
        {
            title: 'Rights clearance is on you',
            body: 'If a generated asset carries a recognisable brand, logo, character or person, ' +
                'obtaining the further clearances that use needs is your responsibility.'
        },
        {
            title: 'Fair-use limits',
            body: 'Output may not be resold as stock, used to train another AI system, or used to ' +
                'generate unlawful, defamatory or infringing content.'
        }
    ];

    /**
     * Answers written from the figures on this page rather than supplied with the
     * design, which shows the list collapsed.
     */
    /**
     * The questions, from the pricing page's FAQ block.
     *
     * An answer is a list of blocks: a string is a paragraph, `{ list: [...] }` a
     * bullet list — several of these answers carry both, and one string per
     * answer could not hold them.
     *
     * KEEP THE FIGURES IN STEP with the packs. These state credit costs, and the
     * authority for those is the credit pack detail rows the table above reads;
     * this text does not follow them.
     */
    readonly faqs: { q: string; a: (string | { list: string[] })[] }[] = [
        {
            q: 'How does the credit system work?',
            a: [
                'Your credits are used across supported IndiaPicture creation tools. Credit consumption depends on the type of generation or action you choose.',
                'For the Starter subscription:',
                { list: ['1 credit per image generation', '2 credits per video generation', '2 credits per stock download', '4 credits per stock video'] },
                'Credit usage can vary by the selected product, tool and generation type. Quarterly plans receive 5% off and yearly plans receive 20% off, which reduces the effective cost per credit further.'
            ]
        },
        {
            q: 'What is the difference between Monthly, Quarterly and Yearly plans?',
            a: [
                'All three provide the same core subscription benefits — the billing commitment and pricing differ.',
                { list: ['Monthly: pay month-to-month, cancel anytime.', 'Quarterly: pay upfront for 3 months and save 5%.', 'Yearly: pay upfront for 12 months and save 20%.'] }
            ]
        },
        {
            q: 'How many credits does video generation use?',
            a: [
                'In the Starter subscription, video generation uses 2 credits per video and stock video uses 4 credits per video.',
                'Credit usage varies by model — the table above lists what each one costs per generation.'
            ]
        },
        {
            q: 'Which AI models and tools are available?',
            a: [
                'The Starter plan includes access to the following:',
                {
                    list: [
                        'Image models: imagen-4, gpt-image-1, gpt-image-2, nano-banana-pro, nano-banana',
                        'Video models: Seedance-1-Pro, HappyHorse-1.0, Wan-2.2-Fast, Wan-2.5',
                        'Upscaling models: Real-ESRGAN, Bria'
                    ]
                }
            ]
        },
        {
            q: 'What can I use my credits for?',
            a: [
                'Depending on your plan and available tools, credits can be used across image generation, video generation, editing tools, stock downloads, upscaling and other supported creation tools. Credit usage depends on the selected generation type or tool.'
            ]
        },
        {
            q: 'Can I use the images and videos commercially?',
            a: [
                'Yes. Every plan includes the Standard commercial licence, which covers web, social, digital advertising and print up to the stated run. Out-of-home, broadcast, cinema and resale merchandise need the Enhanced Licence.'
            ]
        },
        {
            q: 'Do my credits expire?',
            a: [
                'Monthly credits reset at the start of each billing month and do not roll over. Quarterly and yearly plans pool the whole term’s credits, so they can be spent at whatever pace suits the work.'
            ]
        },
        {
            q: 'Can I get a custom plan for my team?',
            a: [
                'Yes. The Custom plan is designed for larger teams that need:',
                { list: ['Custom credit allocation', 'Shared / team credit pools', 'Multiple users', 'Custom commercial terms', 'Invoice billing', 'Dedicated sales support', 'Exclusive Stock Access'] },
                'Talk to us and we will build a plan around your workflow.'
            ]
        }
    ];

    /** Whether an answer block is a bullet list rather than a paragraph. */
    isFaqList(block: string | { list: string[] }): boolean {
        return typeof block !== 'string';
    }

    /** The bullets, for a block isFaqList() has already accepted. */
    faqListItems(block: string | { list: string[] }): string[] {
        return typeof block === 'string' ? [] : block.list;
    }

    /** Which FAQ is open; one at a time, as the design shows them. */
    openFaq: number | null = null;

    toggleFaq(index: number): void {
        this.openFaq = this.openFaq === index ? null : index;
    }

    /**
     * The pack the toggle is currently showing for a tier — the row that would
     * actually be bought. Null when that name is not sold for this duration.
     */
    packIdFor(tier: PricingTier): number | null {
        const t = this.term(tier);
        return t ? t.packId : null;
    }

    /** True when the customer is already on the pack the toggle is showing. */
    isActivated(tier: PricingTier): boolean {
        const id = this.packIdFor(tier);
        return !!id && !!this.activePlanId && Number(id) === Number(this.activePlanId);
    }

    /**
     * Whether the CTA can do anything. A tier can be shown on a duration it is not
     * sold for — the card falls back to a derived yearly figure when a name has no
     * yearly row — and there is no pack id to check out with in that case.
     */
    canCheckout(tier: PricingTier): boolean {
        return !!this.packIdFor(tier) && !this.isActivated(tier);
    }

    ctaLabel(tier: PricingTier): string {
        if (this.isActivated(tier)) {
            return 'Activated Plan';
        }
        return this.packIdFor(tier) ? tier.cta : 'Not sold ' + this.termLabel(this.billing).toLowerCase();
    }

    /**
     * Buy the pack behind a card. The same route the /subscription page uses, so
     * both surfaces check out through one flow.
     *
     * The sheet is dismissed first either way: it is a fixed overlay at z-index
     * 1500, and the sign-in modal would otherwise open behind it.
     */
    openCheckout(tier: PricingTier): void {
        const id = this.packIdFor(tier);
        if (!id || this.isActivated(tier)) {
            return;
        }

        this.close();

        if (this.currentUser) {
            this.router.navigate(['/shop/checkout', id]);
        } else {
            this.modalService.showLoginModal();
        }
    }

    /**
     * How many runs of one tool a tier's credits buy over the selected term.
     *
     * Against the TERM's credits, not a month's: on a yearly plan the honest
     * answer is what the year buys, and quietly dividing it back down to a month
     * would understate the plan the customer is actually looking at.
     */
    compareRuns(tier: PricingTier, per: number): number {
        return Math.floor(this.termCredits(tier) / Math.max(1, per));
    }

    /**
     * Moves the rail one card along.
     *
     * A card's width plus the gap, measured off the rail rather than written
     * down: the card is a third of the track on a wide sheet and most of it on a
     * phone, so a fixed step would overshoot on one and undershoot on the other.
     */
    scrollTiers(direction: number): void {
        const track = this.tierTrack && this.tierTrack.nativeElement;
        if (!track) { return; }

        const card = track.querySelector('.pr-tier') as HTMLElement;
        const gap = card && card.nextElementSibling
            ? (card.nextElementSibling as HTMLElement).offsetLeft - card.offsetLeft - card.offsetWidth
            : 0;
        const step = card ? card.offsetWidth + gap : track.clientWidth;

        track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    /**
     * Keeps the arrows in step with where the rail actually is.
     *
     * Bound to the window as well as the rail: a resize changes how many cards
     * fit without the rail scrolling, and the arrows would otherwise keep
     * answering for the old width.
     */
    @HostListener('window:resize')
    onTierScroll(): void {
        const track = this.tierTrack && this.tierTrack.nativeElement;

        if (!track) {
            this.canScrollTiersLeft = false;
            this.canScrollTiersRight = false;
            return;
        }

        // A pixel of slack: scrollLeft can land fractionally short of the end on
        // a zoomed or high-DPI display, which would leave the arrow enabled.
        const max = track.scrollWidth - track.clientWidth;
        this.canScrollTiersLeft = track.scrollLeft > 1;
        this.canScrollTiersRight = track.scrollLeft < max - 1;
    }

    /**
     * Re-reads the rail after the cards have been laid out.
     *
     * A frame late on purpose: the cards render on the same tick the packs land,
     * and measuring now would measure the loading line they replace.
     */
    private refreshTierScroll(): void {
        setTimeout(() => this.onTierScroll());
    }

    /**
     * One cell of the credit-usage panel.
     *
     * A dash, not "0 credits", where an action has no price on that medium: zero
     * reads as free, which is the opposite of not offered.
     */
    creditLabel(credits: number): string {
        const n = Number(credits) || 0;

        return n ? n + (n === 1 ? ' credit' : ' credits') : '—';
    }

    /** ₹ with Indian digit grouping — "2,76,000", not "276,000". */
    inr(value: number): string {
        return '₹' + (Number(value) || 0).toLocaleString('en-IN');
    }

    /** The line under the headline price, for whichever term is selected. */
    billingLine(tier: PricingTier): string {
        const t = this.term(tier);
        if (!t) { return ''; }

        return t.months > 1
            ? this.inr(t.price) + ' billed upfront · credits pool for the ' + this.termPeriod()
            : this.inr(t.price) + ' billed monthly · cancel anytime';
    }

    close(): void {
        this.closed.emit();
    }

    /** Try again after a failed load, without closing and reopening the dialog. */
    retry(): void {
        this.load();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.close();
    }
}
